import { Helmet } from "@/pages/home/components/Helmet";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { comics, testimonials } from "@/mocks/comics";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState<"intro" | "chapters">("intro");

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">作品未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该漫画不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-all hover:bg-primary-600">
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.categorySlug === comic.categorySlug))
    .slice(0, 4);

  const randomTestimonials = [...testimonials].sort(() => Math.random() - 0.5).slice(0, 3);

  return (
    <>
      <Helmet
        title={`${comic.title}-${comic.category}韩国漫画无删减下拉式阅读-韩漫头条`}
        description={`${comic.title}是${comic.author}创作的${comic.category}韩国漫画，最新章节${comic.latestChapter}。韩漫头条提供无删减下拉式阅读，免费畅读。${comic.description}`}
        keywords={`${comic.title},${comic.category}韩国漫画,韩国漫画,无删减漫画,下拉式漫画`}
        canonicalUrl={`http://uqdww.cn/article/${slug}`}
        ogTitle={`${comic.title} - 韩漫头条`}
        ogDescription={`${comic.title} - ${comic.description.slice(0, 80)}`}
        ogImage={comic.cover}
        schemaJsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": comic.title,
          "description": comic.description,
          "author": {
            "@type": "Person",
            "name": comic.author
          },
          "datePublished": comic.updatedAt,
          "dateModified": comic.updatedAt,
          "image": comic.cover,
          "publisher": {
            "@type": "Organization",
            "name": "韩漫头条",
            "url": "http://uqdww.cn/"
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `http://uqdww.cn/article/${slug}`
          }
        }}
      />
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="container-page">
            <Breadcrumb items={[
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]} />
          </div>
        </section>

        {/* Comic Detail Header */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="container-page">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[280px] shrink-0">
                <div className="rounded-xl overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === "连载中"
                      ? "bg-accent-100 text-accent-700"
                      : "bg-secondary-100 text-secondary-700"
                  }`}>
                    {comic.status}
                  </span>
                  <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-3 leading-tight">
                    {comic.title}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-2">作者：{comic.author}</p>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed mt-4 max-w-xl">
                    {comic.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mt-5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-amber-400 text-sm"></i>
                      <span className="text-sm font-semibold text-foreground-950">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">评分</span>
                    </div>
                    <div className="w-px h-4 bg-background-300"></div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-eye-line text-foreground-500 text-sm"></i>
                      <span className="text-sm font-semibold text-foreground-950">{(comic.views / 10000).toFixed(0)}万</span>
                      <span className="text-xs text-foreground-500">阅读</span>
                    </div>
                    <div className="w-px h-4 bg-background-300"></div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-bookmark-line text-foreground-500 text-sm"></i>
                      <span className="text-sm font-semibold text-foreground-950">{comic.category}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button className="bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-8 py-3 rounded-full whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2">
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </button>
                  <button className="border border-primary-200 hover:border-primary-400 text-primary-600 text-sm font-medium px-8 py-3 rounded-full whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2">
                    <i className="ri-bookmark-line"></i> 加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-page max-w-3xl">
            <div className="flex items-center gap-1 bg-background-200/70 p-1 rounded-full mb-8 w-fit">
              <button
                onClick={() => setActiveTab("intro")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "intro"
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                作品简介
              </button>
              <button
                onClick={() => setActiveTab("chapters")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "chapters"
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                章节列表
              </button>
            </div>

            {activeTab === "intro" && (
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-4">作品简介</h2>
                <div className="bg-background-50 rounded-xl p-6 md:p-8">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {comic.description}
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
                    《{comic.title}》是由{comic.author}创作的{comic.category}类韩国漫画，自连载以来凭借精彩的剧情和出色的画风获得了读者的广泛好评。
                    目前该作品在韩漫头条上提供无删减版本，采用下拉式阅读方式，让读者能够获得流畅舒适的阅读体验。
                  </p>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "类型", value: comic.category },
                      { label: "状态", value: comic.status },
                      { label: "评分", value: `${comic.rating} 分` },
                      { label: "阅读量", value: `${(comic.views / 10000).toFixed(0)}万` },
                    ].map((item) => (
                      <div key={item.label} className="bg-background-100 rounded-lg px-4 py-3">
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm font-semibold text-foreground-950 mt-0.5">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "chapters" && (
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-4">章节列表</h2>
                <div className="bg-background-50 rounded-xl divide-y divide-background-200/70">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-500 font-mono w-8">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm text-foreground-950 font-medium">
                          {i === 0
                            ? comic.latestChapter.replace("（完结）", "")
                            : `第${Math.floor(Math.random() * 200) + 1}话`
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {i === 0 && comic.status === "连载中" && (
                          <span className="text-[10px] text-accent-500 bg-accent-50 px-2 py-0.5 rounded-full font-medium">NEW</span>
                        )}
                        <span className="text-xs text-foreground-500">2026-07-{String(8 - i).padStart(2, "0")}</span>
                      </div>
                    </div>
                  ))}
                  <div className="px-5 py-4 text-center">
                    <button className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                      查看更多章节 <i className="ri-arrow-down-s-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Reader Reviews */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="container-page max-w-3xl">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">读者评论</h2>
            <div className="flex flex-col gap-4">
              {randomTestimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-amber-400 text-[10px]"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="container-page">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <Link
                  key={c.id}
                  to={`/article/${c.slug}`}
                  className="group block bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={c.cover}
                      alt={c.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                      c.status === "连载中"
                        ? "bg-accent-500/90 text-background-50"
                        : "bg-foreground-700/80 text-background-50"
                    }`}>
                      {c.status}
                    </span>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 truncate">{c.title}</h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-foreground-600">{c.author}</span>
                      <span className="text-xs text-primary-500 font-medium whitespace-nowrap">{c.latestChapter}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}